<template>
  <div id="app">
    <AppHeader />
    <main>
      <router-view />
    </main>
    <AppFooter />
    <NotificationToast />
    
    <!-- Floating Chatbot (only show on non-home pages) -->
    <FloatingChatButton v-if="!isHomePage" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { useFavoritesStore } from '@/stores/favorites';
import { useCompareStore } from '@/stores/compare';
import { useCurrencyStore } from '@/stores/currency';
import { useGoogleAuth } from '@/composables/useGoogleAuth';
import { logError } from '@/utils/errors';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import NotificationToast from '@/components/ui/NotificationToast.vue';
import FloatingChatButton from '@/components/chat/FloatingChatButton.vue';

const route = useRoute();
const authStore = useAuthStore();
const cartStore = useCartStore();
const favoritesStore = useFavoritesStore();
const compareStore = useCompareStore();
const currencyStore = useCurrencyStore();
const {
  googleCredential,
  promptOneTap,
  cancelOneTap,
  resetCredential,
} = useGoogleAuth();

const isHomePage = computed(() => route.name === 'Home');
const oneTapAllowedRoutes = new Set([
  'Home',
  'Products',
]);

const shouldShowGoogleOneTap = computed(() => {
  const routeName = typeof route.name === 'string' ? route.name : '';

  return !authStore.token && oneTapAllowedRoutes.has(routeName);
});

const syncGoogleOneTap = async () => {
  if (shouldShowGoogleOneTap.value) {
    await promptOneTap();
    return;
  }

  cancelOneTap();
};

onMounted(async () => {
  // Load user if token exists
  if (authStore.token) {
    await authStore.loadUser();
  }
  
  // Load cart from localStorage
  cartStore.loadCart();
  
  // Load favorites from localStorage
  favoritesStore.loadFavorites();
  
  // Load compare from localStorage
  compareStore.loadCompare();
  
  // Load saved currency preference
  currencyStore.loadSavedCurrency();

  await syncGoogleOneTap();
});

watch(
  () => authStore.token,
  async () => {
    await syncGoogleOneTap();
  },
  { flush: 'post' }
);

watch(googleCredential, async (newCredential) => {
  if (!newCredential || authStore.isLoading) {
    return;
  }

  try {
    const success = await authStore.socialLogin('google', newCredential);

    if (success) {
      cancelOneTap();
    }
  } catch (error) {
    logError('Error procesando Google One Tap', error);
  } finally {
    resetCredential();
  }
});
</script>
